import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  MENU_PANEL_BASE,
  MENU_PANEL_DEFAULT_MAX_HEIGHT_CLASS,
  MENU_PANEL_SCROLL_GUTTER_CLASS,
  MENU_PANEL_SCROLL_VIEWPORT_CLASS,
  MENU_PANEL_STATIC_BODY_CLASS,
} from './menuTokens.js';
import { joinClasses } from './menu.utils.js';

const DEFAULT_PORTAL_POSITION = { top: 0, left: 0 };

/**
 * Panel flotante que se despliega tras una acción — Figma Menu Card (13483:1642).
 * Por defecto: max-height 240px (8426:37662); overflow con `scrollbar-aimsos-auto-sm`
 * y ~8px de gutter top/bottom/right (wrapper `py-2 pr-2` fuera del scroller).
 * El thumb aparece al hover vía CSS (`tailwind-scrollbar-aimsos-plugin`).
 *
 * @param {import('./menuTypes.js').MenuPanelProps} props
 */
const MenuPanel = ({
  children,
  className = '',
  panelRef,
  role = 'menu',
  ariaLabel,
  scrollable = true,
  maxHeightClass = MENU_PANEL_DEFAULT_MAX_HEIGHT_CLASS,
  portal = false,
  portalTarget,
  anchorRef,
  align = 'end',
}) => {
  const [portalPosition, setPortalPosition] = useState(DEFAULT_PORTAL_POSITION);
  const hasDocument = typeof document !== 'undefined';
  const resolvedPortalTarget =
    hasDocument && portalTarget instanceof HTMLElement ? portalTarget : hasDocument ? document.body : null;

  useLayoutEffect(() => {
    if (!portal || !hasDocument || !anchorRef?.current) return undefined;

    const updatePosition = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      setPortalPosition({
        top: rect.bottom + window.scrollY + 8,
        left: align === 'start'
          ? rect.left + window.scrollX
          : rect.right + window.scrollX,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [align, anchorRef, hasDocument, portal]);

  const panel = (
    <div
      ref={panelRef}
      role={role}
      aria-label={ariaLabel}
      className={joinClasses(
        MENU_PANEL_BASE,
        !scrollable ? MENU_PANEL_STATIC_BODY_CLASS : '',
        className,
      )}
      style={
        portal
          ? {
              position: 'absolute',
              top: portalPosition.top,
              left: portalPosition.left,
              transform: align === 'end' ? 'translateX(-100%)' : undefined,
            }
          : undefined
      }
    >
      {scrollable ? (
        <div className={MENU_PANEL_SCROLL_GUTTER_CLASS}>
          <div
            className={joinClasses(
              MENU_PANEL_SCROLL_VIEWPORT_CLASS,
              maxHeightClass,
            )}
          >
            {children}
          </div>
        </div>
      ) : children}
    </div>
  );

  if (portal && resolvedPortalTarget) {
    return createPortal(panel, resolvedPortalTarget);
  }

  return panel;
};

export default MenuPanel;
